<template>
  <MainLayout>
    <div class="lg:flex lg:h-screen lg:flex-col">
      <div class="sm:flex sm:justify-between">
        <router-link to="/teammates">
          <TButtonSvg label="List View" color="white" @click="previousMonth()">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
          </TButtonSvg>
        </router-link>
        <div class="text-lg font-semibold text-gray-900">{{ formatDate(currentMonth, 'MMMM yyyy') }}</div>
        <div>
          <TButton label="Previous Month" color="white" @click="previousMonth()" />
          <TButton class="sm:ml-2" label="Next Month" color="white" @click="nextMonth()" />
        </div>
      </div>
      <div class="mt-2 shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
        <div
          class="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none"
        >
          <div class="flex justify-center bg-white py-2">
            <span>S</span>
            <span class="sr-only sm:not-sr-only">un</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>M</span>
            <span class="sr-only sm:not-sr-only">on</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>T</span>
            <span class="sr-only sm:not-sr-only">ue</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>W</span>
            <span class="sr-only sm:not-sr-only">ed</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>T</span>
            <span class="sr-only sm:not-sr-only">hu</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>F</span>
            <span class="sr-only sm:not-sr-only">ri</span>
          </div>
          <div class="flex justify-center bg-white py-2">
            <span>S</span>
            <span class="sr-only sm:not-sr-only">at</span>
          </div>
        </div>
        <div class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
          <div class="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            <!--
              Always include: "relative py-2 px-3"
              Is current month, include: "bg-white"
              Is not current month, include: "bg-gray-50 text-gray-500"
            -->
            <div
              v-for="day of calendarDays"
              :key="day.toString()"
              :class="
                isDateSame(day, currentMonth, 'month')
                  ? 'relative bg-white py-2 px-3 hover:bg-gray-100'
                  : 'relative bg-gray-50 py-2 px-3 text-gray-500 hover:bg-gray-100'
              "
            >
              <div
                :class="
                  isDateSame(day, currentMonth, 'day') && isDateSame(day, currentDay, 'day')
                    ? 'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white'
                    : ''
                "
              >
                {{ formatDate(day, 'd') }}
              </div>
              <ol v-for="event of events" :key="event.id" class="mt-2">
                <li
                  v-if="
                    formatDate(day, 'd') == formatDate(event.start_time, 'd') &&
                    formatDate(day, 'm') == formatDate(event.start_time, 'm') &&
                    formatDate(day, 'y') == formatDate(event.start_time, 'y')
                  "
                >
                  <router-link :to="`/team/${event.contact_id}`" class="group flex">
                    <p class="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600">
                      {{ event.contact.name }}
                    </p>
                    <div class="ml-3 hidden flex-none text-gray-500 group-hover:text-indigo-600 xl:block">
                      {{ formatDate(event.start_time, 'h:mm a') }}
                    </div>
                  </router-link>
                </li>
              </ol>
            </div>
          </div>
        </div>
        <div class="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
          <button
            v-for="day of calendarDays"
            :key="day.toString()"
            type="button"
            :class="
              isDateSame(day, currentMonth, 'month')
                ? 'flex h-14 flex-col bg-white py-2 px-3 text-gray-900 hover:bg-gray-100 focus:z-10'
                : 'flex h-14 flex-col bg-gray-50 py-2 px-3 text-gray-500 hover:bg-gray-100 focus:z-10'
            "
            @click="currentDay = day"
          >
            <div
              :class="
                isDateSame(day, currentMonth, 'day') && isDateSame(day, currentDay, 'day')
                  ? 'font-semibold text-indigo-600 ml-auto'
                  : 'ml-auto'
              "
            >
              {{ formatDate(day, 'd') }}
            </div>
            <span v-for="event of events" :key="event.id" class="-mx-0.5 mt-auto flex flex-wrap-reverse">
              <span
                v-if="
                  formatDate(day, 'd') == formatDate(event.start_time, 'd') &&
                  formatDate(day, 'm') == formatDate(event.start_time, 'm') &&
                  formatDate(day, 'y') == formatDate(event.start_time, 'y')
                "
                class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-400"
              ></span>
            </span>
          </button>
        </div>
      </div>
      <div class="py-10 px-4 sm:px-6 lg:hidden">
        <ol
          class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black ring-opacity-5"
        >
          <li
            v-for="event of events"
            :key="event.id"
            :class="
              isDateSame(currentDay, event.start_time, 'day') &&
              isDateSame(currentDay, event.start_time, 'month') &&
              isDateSame(currentDay, event.start_time, 'year')
                ? 'group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50'
                : ''
            "
          >
            <div
              v-if="
                isDateSame(currentDay, event.start_time, 'day') &&
                isDateSame(currentDay, event.start_time, 'month') &&
                isDateSame(currentDay, event.start_time, 'year')
              "
              class="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50"
            >
              <div class="flex-auto">
                <p class="font-semibold text-gray-900">{{ event.contact.name }} - {{ event.name }}</p>
                <div class="flex">
                  <!-- Heroicon name: mini/clock -->
                  <svg
                    class="mr-2 h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  {{ formatDate(event.start_time, 'h:mm a') }} - {{ formatDate(event.end_time, 'h:mm a') }}
                </div>
              </div>
              <a
                href="#"
                class="ml-6 flex-none self-center rounded-md border border-gray-300 bg-white py-2 px-3 font-semibold text-gray-700 opacity-0 shadow-sm hover:bg-gray-50 focus:opacity-100 group-hover:opacity-100"
                >Edit<span class="sr-only">, Maple syrup museum</span></a
              >
            </div>
          </li>
        </ol>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import { startOfMonth, startOfWeek, endOfMonth, endOfWeek } from 'date-fns';
import DateUtility from '../../utilities/dates';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';

export default {
  components: {
    MainLayout,
  },
  setup() {
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { loadingSpinnerStore };
  },
  data() {
    return {
      calendarDays: [],
      events: [],
      currentMonth: new Date(),
      currentDay: new Date(),
    };
  },
  mounted() {
    this.getDays();
    this.getEvents();
  },
  methods: {
    ...DateUtility,
    getDays() {
      let firstDayOfMonth = startOfMonth(this.currentMonth);
      let firstDayOfCal = startOfWeek(firstDayOfMonth);
      let lastDayOfMonth = endOfMonth(firstDayOfMonth);
      let lastDayOfCal = endOfWeek(lastDayOfMonth);
      let temp = new Date(firstDayOfCal);
      let days = [temp];
      while (temp < lastDayOfCal && days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      while (days.length < 42) {
        temp.setDate(temp.getDate() + 1);
        days.push(new Date(temp));
      }
      this.calendarDays = days;
    },
    getEvents() {
      const self = this;
      this.loadingSpinnerStore.showLoadingSpinner();
      fetch(`/api/contact_events`)
        .then(response => {
          this.loadingSpinnerStore.hideLoadingSpinner();
          return response.json();
        })
        .then(data => {
          self.events = data;
        });
    },
    nextMonth() {
      this.currentMonth = addMonths(this.currentMonth, 1);
      this.getDays();
    },
    previousMonth() {
      this.currentMonth = subMonths(this.currentMonth, 1);
      this.getDays();
    },
  },
};
</script>
